import React, {ChangeEvent, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {IVehicle, VehicleService} from '@services/Vehicles/Vehicles';
import useServiceHook from '@hooks/useServiceHook';
import DropDownMenu from '@components/inputs/DropDownMenu';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import Label from '@components/inputs/text/Label';
import NumericInput from '@components/inputs/NumericInput';
import VehicleDetails from '@components/panels/VehicleDetails';
import {textBoxStyle} from '@utils/styles/textboxStyle';
import {countrySpecificDistanceUnit, getRootGroupFromAncestry} from '@utils/misc/functions';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import {NavBar} from '@components/navigation/NavBar/NavBar';
import {NewVehicleAction, NewVehicleState} from './newVehicleReducer';
import {CurrentVehicleState} from './currentVehicleDetailsReducer';
import {Country} from '@utils/constants/localisation';
import {SelectedSubscriptionState} from './selectedSubscriptionReducer';
import {logger as SentryLogger} from '@sentry/react';
import {ExchangeReasonState, ExchangeTypes} from "@views/VehicleExchange/exchangeReasonReducer";
import {PlanOptions} from "@utils/planOptions";

interface IFleetioStatus {
  id: string;
  name: string;
  account_id: string;
  color: string;
}

interface INewVehicle {
  navigateTarget: string;
  selectedSubscriptionState: SelectedSubscriptionState;
  currentVehicleState: CurrentVehicleState;
  newVehicleState: NewVehicleState;
  newVehicleDispatcher: React.Dispatch<NewVehicleAction>;
  exchangeReasonState: ExchangeReasonState;
}

export default function NewVehicle({
  navigateTarget,
  selectedSubscriptionState,
  currentVehicleState,
  newVehicleState,
  newVehicleDispatcher,
  exchangeReasonState,
}: INewVehicle) {
    const {custId, country} = useParams() as { custId: string; country: Country };
    const navigate = useNavigate();

    // In AU we want to filter vehicles by their Hub (e.g. NSW / VIC / QLD)
    // In GB, we want to show vehicles from both hubs (Cricklewood and Tower Bridge)
    const searchHub = country === Country.GB
        ? null
        // Root group of vehicle
        : getRootGroupFromAncestry(currentVehicleState.vehicle.group_ancestry);

    //Normally we want to search for vehicles of the same type as the current vehicle
    let searchVehicleType = selectedSubscriptionState.selectedPlan;
    // However for the case of a temp exchange we only want to offer rentals as their temp vehicle
    if (exchangeReasonState.exchangeType === ExchangeTypes.Temporary)
        //Note that Flexi and RAV contracts cannot do temp exchanges.
        // So this code should only be relevant for Flexi-Own subscriptions.
        if (country == Country.GB) {
            searchVehicleType = PlanOptions.UKFlexi;
        } else {
            searchVehicleType = PlanOptions.SplendFlexi;
        }
    // Bool to control the service hook. Run if we have a vehicle type
    const allowVehicleSearch = typeof searchVehicleType === "string" && searchVehicleType.length > 0;

    const [loading, , , vehicles] = useServiceHook(
        VehicleService.getAvailableVehicles,
        [{vehicle_type: searchVehicleType, hub: searchHub}, Country[country]],
        allowVehicleSearch,
        [country]  // Country is from useParams so we add as a dependency.
        // Should never change in the components lifetime
    );

  const [filteredVehicles, setFilteredVehicles] = useState<IVehicle[]>([]);
  useEffect(() => {
    // When we get vehicles from the service, filter them using the reserved_for field
    if (!vehicles || vehicles.length === 0) {
      setFilteredVehicles([]);
      return;
    }
    let filtered: IVehicle[];
    // Keep those with no reserved_for, or reserved_for === custId
    filtered = vehicles.filter((v: IVehicle) => {
        return !v.reserved_for || v.reserved_for === custId;
    });
    // Sort: vehicles reserved for custId at the top
    filtered.sort((a: IVehicle, b: IVehicle) => {
    const aReserved = a.reserved_for === custId ? 1 : 0;
    const bReserved = b.reserved_for === custId ? 1 : 0;
    return bReserved - aReserved;
    });

    setFilteredVehicles(filtered);
    SentryLogger.debug("Filtered vehicles for exchange", {
      all_vehicles: vehicles,
      filtered_vehicles: filtered,
      custId: custId,
      searchHub: searchHub
    })
  }, [vehicles, custId, exchangeReasonState.exchangeType]);

  const selectedVehicle = newVehicleState.selectedVehicle;

  useEffect(() => {
    // Set default starting odometer if not set
    if (selectedVehicle && newVehicleState.startingOdometer === 0) {
      const defaultEndingOdometer = selectedVehicle.current_meter_value?.toString() ?? '0';
      newVehicleDispatcher({ type: 'UPDATE_FIELD', field: 'startingOdometer', value: defaultEndingOdometer });
    }
  }, [selectedVehicle]);

  const onStartingOdometerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    newVehicleDispatcher({ type: 'UPDATE_FIELD', field: 'startingOdometer', value });
  };


  const handleVehicleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const foundVehicle: IVehicle | undefined = filteredVehicles.find((v: any) => v.id === selectedId);
    if (foundVehicle) {
      console.log(foundVehicle)
      console.log(foundVehicle.current_meter_value)
      newVehicleDispatcher({ type: 'UPDATE_SELECTED_VEHICLE', payload: foundVehicle });
      newVehicleDispatcher({ type: 'UPDATE_FIELD', field: 'startingOdometer', value: foundVehicle.current_meter_value.toString() });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  // Submit button is disabled if vehicle ID is not present
  const isSubmitDisabled = !(selectedVehicle?.id && selectedVehicle.id.trim().length > 0);

  return (
    <>
      <NavBar pageTitle="New vehicle details" backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit}>
        {loading ? (
          <LoadingSpinner />
        ) : filteredVehicles && filteredVehicles.length > 0 ? (
          <Section
            childCmp={
              <>
                <Label text="Select a vehicle" requiredFieldInd={true} />
                <DropDownMenu
                  menuName="vehicle"
                  required={true}
                  testId="vehicle-dropdown"
                  choices={filteredVehicles.map((item: IVehicle) => {
                    // If the vehicle is reserved for the customer, append a label
                    const reservedLabel = item.reserved_for === custId ? ' (Reserved for customer)' : '';
                    return {
                      label: `${item.license_plate} - ${item.make} ${item.model}${reservedLabel}`,
                      value: item.id,
                      enabled: true,
                    };
                  })}
                  onSelect={handleVehicleSelect}
                />
              </>
            }
          />
        ) : (
          <p>No vehicles found.</p>
        )}

        {selectedVehicle && (
          <>
            <p>Please confirm that vehicle details are correct</p>
            <VehicleDetails
              make={selectedVehicle.make}
              model={selectedVehicle.model}
              year={selectedVehicle.year}
              color={selectedVehicle.color}
              licensePlate={selectedVehicle.license_plate}
              vin={selectedVehicle.vin}
              bodyType={selectedVehicle.body_type}
              startingMiles={selectedVehicle.current_meter_value}
            />

            <Section
              childCmp={
                <>
                  <Label text={`Starting ${countrySpecificDistanceUnit(country)}`} requiredFieldInd={true} />
                  <NumericInput
                    style={textBoxStyle}
                    name="startingOdometer"
                    dataTestId="startingOdometer"
                    value={newVehicleState.startingOdometer}
                    min={newVehicleState.startingOdometer}
                    step="any"
                    required={true}
                    onChange={onStartingOdometerChange}
                  />
                </>
              }
            />
          </>
        )}

        <SubmitBtn text="Next" breadcrumbObject={breadcrumbObject} disabled={isSubmitDisabled} />
      </form>
    </>
  );
}

const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: '2.5vh 0vw' }}>{childCmp}</div>
);

const LoadingSpinner = () => (
  <div style={{ height: '5vh' }}>
    <Elliptical />
  </div>
);
